<template>
    <DForm @submit.prevent="submitForm">
        <div class="product-form">
            <DCard
                :title="__('products.products.form.headline.product')"
                padding
            >
                <DFormRow
                    v-if="productGroupsAndProducts.length > 0"
                    :label="__('products.products.form.addon_copy')"
                    :error="errors.product_copy_id"
                >
                    <DSelectGroup
                        id="product_copy_id"
                        v-model="form.product_copy_id"
                        :option-groups="productGroupsAndProducts"
                        :placeholder="__('products.products.form.none')"
                        :is-valid="isFormFieldValid('product_copy_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="productGroupsAndProducts.length > 0"
                    :label="__('products.products.form.copy_product_plans')"
                    :error="errors.copy_product_plans"
                    :hint="__('products.products.form.help_text.copy_product_plans')"
                >
                    <DSwitch
                        id="copy-product-plans"
                        v-model="form.copy_product_plans"
                        color="success"
                        variant="3d"
                        :disabled="!form.product_copy_id"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.name')"
                    is-required
                    :error="errors.name"
                >
                    <DInput
                        id="name"
                        v-model="form.name"
                        :placeholder="__('products.products.form.name')"
                        :is-valid="isFormFieldValid('name')"
                        maxlength="255"
                    />

                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.productAddonGroup')"
                    is-required
                    :error="errors.product_group_id"
                >
                    <DSelect
                        id="product_group"
                        v-model="form.product_group_id"
                        :options="productGroups"
                        :placeholder="__('products.products.form.productAddonGroup')"
                        :is-valid="isFormFieldValid('product_group_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.productCode')"
                    :error="errors.name_short"
                >
                    <DInput
                        id="name_short"
                        v-model="form.name_short"
                        :placeholder="__('products.products.form.productCode')"
                        :is-valid="isFormFieldValid('name_short')"
                        maxlength="35"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.retired')"
                    :error="errors.retired"
                >
                    <DSwitch
                        id="switch-retired"
                        v-model="form.retired"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.service_request.label')"
                    :error="errors.service_request"
                    :hint="__('products.products.form.service_request.description')"
                >
                    <DSwitch
                        id="switch-service-request"
                        v-model="form.service_request"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.can_be_moved')"
                    :error="errors.can_be_moved"
                >
                    <DSwitch
                        id="switch-can-be-moved"
                        v-model="form.can_be_moved"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.order')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.requires_domain.label')"
                    :error="errors.requires_domain"
                    :hint="__('products.products.form.requires_domain.description')"
                >
                    <DSelect
                        id="requires_domain"
                        v-model="form.requires_domain"
                        :options="requiresDomainOptions"
                        :is-valid="isFormFieldValid('requires_domain')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.addon_order_limit')"
                    :error="errors.order_limit"
                >
                    <DInput
                        id="addon_order_limit"
                        v-model="form.order_limit"
                        type="number"
                        :is-valid="isFormFieldValid('order_limit')"
                        min="-1"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.fulfillment')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.fulfillmentStrategy')"
                    :error="errors.fulfillment_strategy_id"
                    :hint="__('products.products.form.fulfillmentStrategy_hint')"
                >
                    <DSelect
                        id="fulfillment_strategy"
                        v-model="form.fulfillment_strategy_id"
                        :options="fulfillmentStrategies"
                        :placeholder="__('products.products.form.fulfillmentStrategy')"
                        :is-valid="isFormFieldValid('fulfillment_strategy_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.termination_rule_id')"
                    :error="errors.termination_rule_id"
                    is-required
                >
                    <DSelect
                        id="termination_rule"
                        v-model="form.termination_rule_id"
                        :options="terminationRules"
                        :placeholder="__('products.products.form.termination_rule_id')"
                        :is-valid="isFormFieldValid('termination_rule_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isInvoiceBeforeRenewalEnabled === false"
                    :label="__('products.products.form.lead_time')"
                    :error="errors.lead_time"
                    :hint="__('products.products.form.lead_time_helper_text')"
                >
                    <DInput
                        id="lead_time"
                        v-model="form.lead_time"
                        type="number"
                        :placeholder="__('products.products.form.lead_time')"
                        :is-valid="isFormFieldValid('lead_time')"
                        min="0"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.fulfillmentModule')"
                    :error="errors.fulfillment_module"
                >
                    <DFieldLoading
                        v-if="isLoadingFulfillmentGroup"
                        :is-loading="isLoadingFulfillmentGroup"
                    />
                    <DSelect
                        v-else
                        id="fulfillment_module"
                        v-model="form.fulfillment_module"
                        :options="fulfillmentModules"
                        :placeholder="__('products.products.form.fulfillmentModule')"
                        :is-valid="isFormFieldValid('fulfillment_module')"
                        :disabled="isLoadingFulfillmentGroup || isLoadingFulfillmentOptions"
                        @change="changeFulfillmentModule"
                    />
                </DFormRow>
                <DFormRow
                    v-if="fulfillmentAccountGroups.length > 0"
                    :label="__('products.products.form.fulfillmentGroup')"
                    :error="errors.fulfillment_account_group_id"
                >
                    <DFieldLoading
                        v-if="isLoadingFulfillmentOptions"
                        :is-loading="isLoadingFulfillmentOptions"
                    />
                    <DSelect
                        v-else
                        id="fulfillment_account_group"
                        v-model="form.fulfillment_account_group_id"
                        :options="fulfillmentAccountGroups"
                        :placeholder="__('products.products.form.fulfillmentGroup')"
                        :is-valid="isFormFieldValid('fulfillment_account_group_id')"
                        :disabled="isLoadingFulfillmentGroup || isLoadingFulfillmentOptions"
                        @change="changeFulfillmentAccountGroup"
                    />
                </DFormRow>
                <DFormRow
                    v-if="fulfillmentAccountGroupsError"
                    label=""
                >
                    <DAlert color="danger">
                        {{ fulfillmentAccountGroupsError }}
                    </DAlert>
                </DFormRow>
                <div v-if="configOptions.length > 0">
                    <hr>
                    <DRow>
                        <DCol class="mb-2">
                            <h2 class="h4 mb-0">
                                {{ __('products.products.form.config') }}
                            </h2>
                            <small class="text-muted">{{ __(`products.products.form.sorting.${configValuesSortingType}`) }}</small>
                        </DCol>
                    </DRow>
                    <DFormRow
                        v-for="(row, index) in configOptions"
                        v-show="row.visible"
                        :key="index"
                        :label="row.translated_name"
                        :is-required="row.required"
                        :hint="row.description"
                        :error="errors[`config_values.${row.name}.value`]"
                    >
                        <DSwitch
                            v-if="row.type === 'bool'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            color="success"
                            variant="3d"
                            :class="{'is-invalid': !!errors[`config_values.${row.name}.value`]}"
                        />
                        <DInput
                            v-if="row.type === 'string'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DInput
                            v-if="row.type === 'integer'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DInput
                            v-if="row.type === 'email'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DFormTextarea
                            v-if="row.type === 'text' || row.type === 'array'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                            rows="8"
                        />
                        <DSelect
                            v-if="row.type === 'list'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :options="row.options"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                        <DSelect
                            v-if="row.type === 'api'"
                            :id="`option_${row.name}`"
                            v-model="form.config_values[row.name].value"
                            :options="apiOptions[row.name]"
                            :is-valid="isFormFieldValid(`config_values.${row.name}.value`)"
                        />
                    </DFormRow>
                    <hr>
                </div>
                <DFormRow
                    :label="__('products.products.form.dnsTemplate')"
                    :error="errors.dns_template_id"
                >
                    <DSelect
                        id="dns_template"
                        v-model="form.dns_template_id"
                        :options="dnsTemplates"
                        :placeholder="__('products.products.form.dnsTemplate')"
                        :is-valid="isFormFieldValid('dns_template_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.onehome_configuration')"
                    :error="errors.onehome_configuration"
                >
                    <DJsonTextarea
                        id="onehome_configuration"
                        v-model="form.onehome_configuration"
                        :placeholder="__('products.products.form.onehome_configuration')"
                        :is-valid="isFormFieldValid('onehome_configuration')"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.upgrades')"
                padding
            >
                <ProductChangesSelection
                    :label="__('products.products.form.upgrades')"
                    :product-id="product.id ?? null"
                    :multiple="true"
                    :selected-products="form.selectedUpgrades"
                    :required="false"
                    :fulfillment-module="form.fulfillment_module"
                    :fulfillment-account-group-id="form.fulfillment_account_group_id"
                    :errors="errors"
                    @update="updateSelectedUpgrades"
                />
                <ProductChangesSelection
                    :label="__('products.products.form.downgrades')"
                    :product-id="product.id ?? null"
                    :multiple="true"
                    :selected-products="form.selectedDowngrades"
                    :required="false"
                    :fulfillment-module="form.fulfillment_module"
                    :fulfillment-account-group-id="form.fulfillment_account_group_id"
                    :errors="errors"
                    @update="updateSelectedDowngrades"
                />
                <DFormRow
                    :label="__('products.products.form.headline.product_classes')"
                    :is-required="false"
                >
                    <DMultiSelect
                        id="product_classes"
                        :options="selectedProductClassesOptions()"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        @update="updateSelectedProductClasses"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.invoice')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.invoice_text')"
                    :error="errors.invoice_text"
                    :hint="__('products.products.form.invoice_text_helper_text')"
                >
                    <DFormTextarea
                        id="invoice_text"
                        v-model="form.invoice_text"
                        :placeholder="__('products.products.form.invoice_text')"
                        rows="5"
                    />
                    <TranslationLink
                        :tenant-languages="tenantLanguages"
                        :route="$route('products.products.translations', {product: product?.id ?? 0})"
                        :default-tenant-language="defaultTenantLanguage"
                    />
                </DFormRow>
                <DFormRow
                    v-if="userPermissions.includes('product.assign_cost_objects')"
                    :label="__('products.products.form.accountingProduct')"
                    is-required
                    :error="errors.accounting_product_id"
                >
                    <DSelect
                        id="accounting_product"
                        v-model="form.accounting_product_id"
                        :options="accountingProducts"
                        :placeholder="__('products.products.form.accountingProduct')"
                        :is-valid="isFormFieldValid('accounting_product_id')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="userPermissions.includes('product.assign_cost_objects')"
                    :label="__('products.products.form.intercompany_accounting_product')"
                    is-required
                    :error="errors.intercompany_accounting_product_id"
                    :hint="__('products.products.form.help_text.intercompany_accounting_product')"
                >
                    <DSelect
                        id="intercompany_accounting_product_id"
                        v-model="form.intercompany_accounting_product_id"
                        :options="intercompanyAccountingProducts"
                        :placeholder="__('products.products.form.intercompany_accounting_product')"
                        :is-valid="isFormFieldValid('intercompany_accounting_product_id')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.invoice_behaviour')"
                    :error="errors.invoice_behaviour"
                    :hint="__('products.products.form.invoice_behaviour_hint')"
                >
                    <DSelect
                        id="invoice_behaviour"
                        v-model="form.invoice_behaviour"
                        :options="invoiceBehaviours"
                        :placeholder="__('products.products.form.invoice_behaviour')"
                        :is-valid="isFormFieldValid('invoice_behaviour')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.disable_invoice_before_renewal.label')"
                    :error="errors.disable_invoice_before_renewal"
                    :hint="__('products.products.form.disable_invoice_before_renewal.description')"
                >
                    <DSwitch
                        id="disable-invoice-before-renewal"
                        v-model="form.disable_invoice_before_renewal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.align_billing_period_with_parent.label')"
                    :error="errors.align_billing_period_with_parent"
                    :hint="__('products.products.form.align_billing_period_with_parent.description')"
                >
                    <DSwitch
                        id="align-billing-period-with-parent"
                        v-model="form.align_billing_period_with_parent"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.billing_type.label')"
                    :error="errors.billing_type"
                    :hint="__('products.products.form.billing_type.description')"
                >
                    <DSelect
                        id="billing_type"
                        v-model="form.billing_type"
                        :options="billingTypes"
                        :is-valid="isFormFieldValid('billing_type')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="$page.props.tenantSettings.automatic_price_adjustment === true"
                    :label="__('products.products.form.automatic_price_adjustment')"
                    :error="errors.automatic_price_adjustment"
                    :hint="__('products.products.form.automatic_price_adjustment_explanation')"
                >
                    <DSwitch
                        id="switch-automatic-price-adjustment"
                        v-model="form.automatic_price_adjustment"
                        color="success"
                        variant="3d"
                        @update:checked="updateAutomaticPrice"
                    />
                </DFormRow>
                <DFormRow
                    v-if="$page.props.tenantSettings.automatic_price_adjustment === true && form.automatic_price_adjustment === true"
                    :label="__('products.products.form.maximum_annual_price')"
                    :error="errors.maximum_annual_price"
                    :hint="__('products.products.form.maximum_annual_price_explanation')"
                >
                    <DInput
                        id="max_annual_price"
                        v-model="form.maximum_annual_price"
                        :is-valid="isFormFieldValid('maximum_annual_price')"
                        type="number"
                        step="0.001"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.accounting_information')"
                    :error="errors.accounting_information"
                >
                    <DInput
                        id="accounting_information"
                        v-model="form.accounting_information"
                        :placeholder="__('products.products.form.accounting_information')"
                        type="string"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.termination')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.termination_on_dunning')"
                    :error="errors.termination_on_dunning"
                    :hint="__('products.products.form.termination_on_dunning_explanation')"
                >
                    <DSwitch
                        id="switch-termination-on-dunning"
                        v-model="form.termination_on_dunning"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.winback_ticket_on_termination.label')"
                    :error="errors.winback_ticket_on_termination"
                    :hint="__('products.products.form.winback_ticket_on_termination.description')"
                >
                    <DSwitch
                        id="winback-ticket-on-termination"
                        v-model="form.winback_ticket_on_termination"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('products.products.form.manual_termination_disallowed.label')"
                    :error="errors.manual_termination_disallowed"
                    :hint="__('products.products.form.manual_termination_disallowed.description')"
                >
                    <DSwitch
                        id="switch-manual-termination-disallowed"
                        v-model="form.manual_termination_disallowed"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    v-if="isShortTerminationPeriodSet"
                    :label="__('products.products.form.excluded_customer_protection_law')"
                    :error="errors.excluded_customer_protection_law"
                >
                    <DSwitch
                        id="switch-excluded-customer-protection-law"
                        v-model="form.excluded_customer_protection_law"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DCard
                :title="__('products.products.form.headline.suspension')"
                padding
            >
                <DFormRow
                    :label="__('products.products.form.suspend_with_parent.label')"
                    :error="errors.suspend_with_parent"
                    :hint="__('products.products.form.suspend_with_parent.description')"
                >
                    <DSwitch
                        id="switch-suspend-with-parent"
                        v-model="form.suspend_with_parent"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
        </div>
        <RetiredWarningModal
            :show="showRetiredWarningModal"
            :linked-products="linkedProducts"
            translation-key="products.products.retired_addon_warning_modal"
            @confirm="submitFormAfterRetiredWarning"
            @close="showRetiredWarningModal = false"
        />
        <DFormControls
            v-model="form.activity_log_comment"
            :cancel-route="$route('products.product-addons.index')"
            :dirty="form.isDirty"
            :show-change-reason-input="true"
            @submit="submitForm"
        />
    </DForm>
</template>

<script>

import DFieldLoading from '~@/Components/Common/DFieldLoading'
import TranslationLink from '../../Settings/TranslationLink.vue'
import DSelectGroup from '~@/Components/Common/Forms/DSelectGroup.vue'
import ProductChangesSelection from '~@/Components/Products/Products/ProductChangesSelection'
import RetiredWarningModal from '~@/Components/Products/RetiredWarningModal.vue'

export default {
    name: 'ProductForm',
    components: {
        DSelectGroup,
        TranslationLink,
        DFieldLoading,
        ProductChangesSelection,
        RetiredWarningModal
    },
    props: {
        errors: {
            type: Object,
            default: () => { return {} }
        },
        product: {
            type: Object,
            default: () => { return {} }
        },
        productGroupsAndProducts: {
            type: Array,
            default () {
                return []
            }
        },
        productGroups: {
            type: Array,
            default: () => { return [] }
        },
        dnsTemplates: {
            type: Array,
            default: () => { return [] }
        },
        accountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        intercompanyAccountingProducts: {
            type: Array,
            default: () => { return [] }
        },
        fulfillmentStrategies: {
            type: Array,
            default: () => { return [] }
        },
        invoiceBehaviours: {
            type: Array,
            default: () => { return [] }
        },
        isShortTerminationPeriodSet: {
            type: Boolean,
            default: () => { return false }
        },
        defaultTenantLanguage: {
            type: String,
            default: ''
        },
        tenantLanguages: {
            type: Array,
            default: () => { return [] }
        },
        isInvoiceBeforeRenewalEnabled: {
            type: Boolean,
            default: false
        },
        preselectedDowngrades: {
            type: Array,
            default: () => { return [] }
        },
        preselectedUpgrades: {
            type: Array,
            default: () => { return [] }
        },
        terminationRules: {
            type: Array,
            default: () => { return [] }
        },
        productClasses: {
            type: Array,
            default: () => { return [] }
        },
        billingTypes: {
            type: Array,
            default: () => { return [] }
        },
        linkedProducts: {
            type: Array,
            default: () => { return [] }
        },
        requiresDomainOptions: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['form-submitted'],
    data () {
        return {
            form: this.$inertia.form({
                name: (this.product?.name && this.productGroupsAndProducts.length > 0)
                    ? 'Copy of ' + this.product.name
                    : (this.product?.name ?? null),
                name_short: this.product?.name_short ?? null,
                invoice_text: this.product?.invoice_text ?? null,
                onehome_configuration: this.product?.onehome_configuration ?? null,
                product_group_id: this.product?.product_group_id ?? null,
                retired: this.product?.retired ?? false,
                service_request: this.product?.service_request ?? false,
                can_be_moved: this.product?.can_be_moved ?? true,
                invoice_behaviour: this.product?.invoice_behaviour ?? null,
                termination_on_dunning: this.product?.termination_on_dunning ?? true,
                winback_ticket_on_termination: this.product?.winback_ticket_on_termination ?? false,
                manual_termination_disallowed: this.product?.manual_termination_disallowed ?? false,
                dns_template_id: this.product?.dns_template_id ?? null,
                accounting_product_id: this.product?.accounting_product_id ?? null,
                intercompany_accounting_product_id: this.product?.intercompany_accounting_product_id ?? null,
                fulfillment_strategy_id: this.product?.fulfillment_strategy_id ?? 'immediate',
                lead_time: this.product?.lead_time ?? 0,
                fulfillment_module: this.product?.fulfillment_module ?? null,
                fulfillment_account_group_id: this.product?.fulfillment_account_group_id ?? null,
                config_values: this.product?.config_values ?? [],
                excluded_customer_protection_law: this.product?.excluded_customer_protection_law ?? false,
                automatic_price_adjustment: this.product?.automatic_price_adjustment ?? false,
                maximum_annual_price: this.product?.maximum_annual_price ?? null,
                requires_domain: this.product?.requires_domain ?? 'never',
                disable_invoice_before_renewal: this.product?.disable_invoice_before_renewal ?? false,
                align_billing_period_with_parent: this.product?.align_billing_period_with_parent ?? false,
                suspend_with_parent: this.product?.suspend_with_parent ?? false,
                billing_type: this.product?.billing_type ?? null,
                accounting_information: this.product?.accounting_information ?? null,
                is_addon: true,
                order_limit: this.product?.order_limit ?? null,
                is_create: Object.keys(this.product).length === 0 || this.productGroupsAndProducts.length > 0,
                activity_log_comment: null,
                product_copy_id: this.product?.id ?? null,
                copy_product_plans: false,
                termination_rule_id: this.product?.termination_rule_id ?? null,
                selectedDowngrades: this.preselectedDowngrades ?? [],
                selectedUpgrades: this.preselectedUpgrades ?? [],
                product_classes: Array.isArray(this.product.classes)
                    ? this.product.classes.map((productClass) => productClass.id)
                    : []
            }),
            configValuesSortingType: null,
            fulfillmentModules: [],
            fulfillmentAccountGroups: [],
            fulfillmentAccountGroupsError: null,
            configOptions: [],
            apiOptions: {},
            oldConfigOptions: {
                module: this.product?.fulfillment_module ?? null,
                values: this.product?.config_values ?? []
            },
            upgradeListKey: 0,
            isLoadingFulfillmentGroup: false,
            isLoadingFulfillmentOptions: false,
            activity_log_comment: null,
            showRetiredWarningModal: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },

    watch: {
        form: {
            handler () {
                this.setOptionVisibility()
            },
            deep: true,
            immediate: true
        },
        'form.product_copy_id': function (newProductCopyId, oldProductCopyId) {
            if (newProductCopyId !== oldProductCopyId) {
                if (!newProductCopyId) {
                    this.form.copy_product_plans = false
                }
                this.selectProductToCopyFrom(newProductCopyId)
            }
        }
    },
    created () {
        this.getFulfillmentModules()
    },
    methods: {
        changeFulfillmentModule () {
            this.form.fulfillment_account_group_id = null
            this.form.config_values = []
            this.configOptions = []
            this.apiOptions = {}
            this.getFulfillmentAccountGroups()
        },
        changeFulfillmentAccountGroup () {
            this.form.config_values = []
            this.configOptions = []
            this.apiOptions = {}
            this.getConfig()
        },
        getFulfillmentModules () {
            this.fulfillmentModules = []

            window.axios.get(this.$route('products.products.available-addon-modules'))
                .then(response => {
                    this.fulfillmentModules.push({ label: 'None', value: null })
                    response.data.forEach((value) => this.fulfillmentModules.push({ label: value, value: value }))
                    this.getFulfillmentAccountGroups()
                })
        },
        getFulfillmentAccountGroups () {
            this.fulfillmentAccountGroups = []
            this.fulfillmentAccountGroupsError = null

            if (this.form.fulfillment_module) {
                this.isLoadingFulfillmentGroup = true

                window.axios.get(this.$route('products.products.account-groups', { module: this.form.fulfillment_module }))
                    .then(response => {
                        if (!response.data.error) {
                            this.fulfillmentAccountGroups.push({ label: 'None', value: null })
                            response.data.forEach((group) => this.fulfillmentAccountGroups.push({ label: group.name, value: group.id }))
                            this.getConfig()
                        } else {
                            this.fulfillmentAccountGroupsError = response.data.error
                        }

                        this.isLoadingFulfillmentGroup = false
                    })
            } else {
                this.configOptions = []
                this.apiOptions = {}
            }
        },
        getConfig () {
            this.configOptions = []
            this.apiOptions = {}

            if (this.form.fulfillment_module && this.form.fulfillment_account_group_id) {
                this.isLoadingFulfillmentOptions = true
                this.form.config_values = {}
                if (this.form.fulfillment_module === this.oldConfigOptions.module) {
                    const values = this.oldConfigOptions.values

                    values.forEach((row) => {
                        this.form.config_values[row.key] = {
                            key: row.key,
                            type: row.type,
                            value: row.type === 'bool' ? row.value === '1' : row.value,
                            dependencyVisibilityOptions: [],
                            visible: true
                        }
                    })
                }

                window.axios.get(this.$route('products.products.config', {
                    module: this.form.fulfillment_module,
                    fulfillmentAccountGroupId: this.form.fulfillment_account_group_id
                }))
                    .then(response => {
                        this.isLoadingFulfillmentOptions = false
                        this.configValuesSortingType = response.data.sorting_type

                        response.data.options.forEach((row) => {
                            if (!Object.prototype.hasOwnProperty.call(this.form.config_values, row.name)) {
                                this.form.config_values[row.name] = {
                                    key: row.name,
                                    type: row.type,
                                    value: row.value,
                                    dependencyVisibilityOptions: row.dependencyVisibilityOptions,
                                    visible: true
                                }
                            }

                            if (row.type === 'api') {
                                this
                                    .getOptionsFromApi(row.name, row.url)
                                    .then(() => this.configOptions.push(row))
                            } else {
                                this.configOptions.push(row)
                            }
                        })

                        this.setOptionVisibility()
                    })
            }
        },
        getOptionsFromApi (name, url) {
            return window.axios.get(url)
                .then(response => { this.apiOptions[name] = response.data })
        },
        submitForm () {
            if (this.form.retired && !this.product?.retired && this.linkedProducts.length > 0) {
                this.showRetiredWarningModal = true
                return
            }

            this.$emit('form-submitted', this.form)
        },
        submitFormAfterRetiredWarning () {
            this.showRetiredWarningModal = false
            this.$emit('form-submitted', this.form)
        },
        updateSelectedDowngrades (values) {
            this.form.selectedDowngrades = values
        },
        updateSelectedUpgrades (values) {
            this.form.selectedUpgrades = values
        },
        updateAutomaticPrice () {
            if (this.form.automatic_price_adjustment === false) {
                this.form.maximum_annual_price = null
            }
        },
        setOptionVisibility () {
            for (const option of this.configOptions) {
                option.visible = this.optionVisible(option)
            }
        },
        optionVisible (option) {
            const dependencies = option.dependencyVisibilityOptions

            for (const configOption of this.configOptions) {
                if (configOption.name in dependencies && this.form.config_values[configOption.name].value !== dependencies[configOption.name]) {
                    return false
                }
            }

            return true
        },
        selectProductToCopyFrom (value) {
            if (value) {
                return this.$inertia.get(this.$route('products.product-addons.copy', { product: value }))
            }

            return this.$inertia.get(this.$route('products.product-addons.create'))
        },
        updateSelectedProductClasses (values) {
            this.form.product_classes = values
        },
        selectedProductClassesOptions () {
            if (this.product.id && this.product.classes) {
                const selectedClasses = this.product.classes.map((productClass) => productClass.id)
                return this.productClasses
                    .map((option) => ({ ...option, selected: selectedClasses.includes(option.value) }))
            }

            return this.productClasses
        }
    }
}
</script>
<style lang="scss" scoped>
.product-form {
    display: grid;
    grid-gap: 25px;
}
</style>
